<script lang="ts" setup>
import { computed, ref, render, watch, h, onMounted } from 'vue';
import { useElementSize } from '@vueuse/core';

import type { HotelAmenityData } from '@apps/main/new-desktop/core/models';

import { AppSvgIcon } from '@libs/ui';
import type { AppSvgIconSize } from '@libs/ui';

interface HotelAmenityDataWithSize extends HotelAmenityData {
  size: number;
}

interface HotelAmenitiesProps {
  amenities: HotelAmenityData[][];
}

const props = defineProps<HotelAmenitiesProps>();

const amenitiesAmenitiesRef = ref(null);
const amenitiesWithSizes = ref<HotelAmenityDataWithSize[]>([]);
const { width } = useElementSize(amenitiesAmenitiesRef);

watch(props, () => {
  updateAmenitiesWithSizes();
});

onMounted(() => {
  updateAmenitiesWithSizes();
});

const iconWidth = 18;
const iconAmenitySpacing = 8;
const amenityAmenitySpacing = 16;
const sidePadding = 12;

const mobileAmenities = computed<HotelAmenityDataWithSize[]>(() => {
  const result: HotelAmenityDataWithSize[] = [];
  let currentWidth = 0;

  amenitiesWithSizes.value.forEach((amenity) => {
    if (currentWidth + amenity.size < width.value || result.length === 0) {
      result.push(amenity);
      currentWidth += amenity.size;
    }
  });

  return result;
});

function updateAmenitiesWithSizes(): void {
  amenitiesWithSizes.value = props.amenities
    .flat()
    .map((amenity: HotelAmenityData) => ({
      ...amenity,
      size: getElementSize(amenity),
    }));
}

function getElementSize(amenity: HotelAmenityData): number {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.visibility = 'hidden';

  container.style.whiteSpace = 'nowrap';
  document.body.appendChild(container);

  const amenityDiv = h(
    'div',
    { style: 'font: var(--font-body-s-light)' },
    amenity.value
  );

  render(amenityDiv, container);

  const width = container.offsetWidth;

  document.body.removeChild(container);
  return (
    width + iconWidth + iconAmenitySpacing + amenityAmenitySpacing + sidePadding
  );
}
</script>

<template>
  <div
    ref="amenitiesAmenitiesRef"
    class="hotel-amenities"
    :style="{
      '--icon-text-gap': iconAmenitySpacing + 'px',
      '--amenities-gap': amenityAmenitySpacing + 'px',
    }"
  >
    <div
      v-for="amenity in mobileAmenities"
      :key="amenity.value"
      class="amenity"
    >
      <AppSvgIcon
        :name="amenity.icon"
        :size="iconWidth.toString() as AppSvgIconSize"
        color="color-neutral-60"
      />
      <span class="amenity-text">{{ amenity.value }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hotel-amenities {
  display: flex;
  gap: var(--amenities-gap);

  .amenity {
    display: flex;
    align-items: center;
    gap: var(--icon-text-gap);

    .amenity-text {
      white-space: nowrap;
      color: var(--color-secondary-60);
      @include font-body-s-light;
    }
  }
}
</style>
